import React, { useEffect, useMemo, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect, useDispatch, useSelector } from 'react-redux'
import { last, isEmpty } from 'lodash'
import cn from 'classnames'
import SegmentedControlV2 from '../../components/segmentedControl/SegmentedControl'
import GratuityHeader from '../../components/GratuityHeader'
import NumberPadV2 from '../../components/NumberPadV2'
import SelectionGroup from './SelectionGroup'

import makeTenderSelections from './utils/makeTenderSelections'
import { getOrderInProgress } from '../../selectors/order'
import { centsToDollars } from '../../utils/formatters'
import useNumberPad from '../../hooks/useNumberPad'
import useCreateOrderInProgress from '../../hooks/useCreateOrderInProgress'
import { numberAsCurrency } from '../../utils/orderUtils'

import styles from './index.module.scss'

import { clearOrderInProgress, clearTipOrderInProgress } from '../../actions/order'
import VNLoader from '../../VNLoader/components/VNLoader'
import { setCurrentPaymentFlow, updateTransactionLifecycle } from '../../VNCustomerFacingDisplay/ActionCreators'
import { CFD_EVENTS, CFD_POS_PAYMENT_FLOWS } from '../../VNCustomerFacingDisplay/Enums'
import VNConcessionsNavBar from '../../components/VNConcessionsNavBar'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import VNBottomButton from '../../components/VNBottomButton'
import { ToastManager } from '../../utils/toastManager'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { isAuthFailedTab, isTabbed } from '../../utils/orderStates'

const splitTenderChoices = [
  { label: 'Evenly', value: 'evenly' },
  { label: 'Percent', value: 'percent' },
  { label: 'Custom', value: 'custom' },
]

const SplitTender = ({
  balanceDueDisplay,
  className,
  effectiveBalanceDueInCents,
  order,
  orderId,
  onConfirm,
  onGoBack,
  lastPaymentId,
  totalAmountInCents,
  totalDisplay,
}) => {
  const [createOrder] = useCreateOrderInProgress()
  const [shouldAnimatePop, setShouldAnimatePop] = useState(false)
  const [shouldAnimateSlide, setShouldAnimateSlide] = useState(false)
  const [selectedTenderType, setSelectedTenderType] = useState('evenly')
  const [selectedTenderAmount, setSelectedTenderAmount] = useState(undefined)
  const [selectionItemsEvenly, setSelectionItemsEvenly] = useState(
    makeTenderSelections('evenly', totalAmountInCents, effectiveBalanceDueInCents),
  )
  const [selectionItemsPercent, setSelectionItemsPercent] = useState(makeTenderSelections('percent', totalAmountInCents))
  const selectionItemsCustom = []
  const customEntry = selectedTenderType === 'custom'
  const isMobile = useGetIsMobileScreen()
  const dispatch = useDispatch()
  const keepOrderInProgress = useMemo(() => {
    if (isTabbed(order) || isAuthFailedTab(order)) {
      return true
    }

    if (lastPaymentId) {
      return true
    }
    return false
  }, [order, lastPaymentId])

  const [customOutput, onCustomInput, customRawValue, customIsValid] = useNumberPad(
    0,
    centsToDollars,
    (value) => parseInt(value) <= effectiveBalanceDueInCents && parseInt(value) > 0,
    6,
  )
  const cfdMode = useSelector(getDeviceMode)

  useEffect(() => {
    if (isEmpty(order)) {
      createOrder()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setSelectionItemsEvenly(makeTenderSelections('evenly', totalAmountInCents, effectiveBalanceDueInCents))
    setSelectionItemsPercent(makeTenderSelections('percent', totalAmountInCents, effectiveBalanceDueInCents))
  }, [totalAmountInCents, effectiveBalanceDueInCents])

  const selectionItems = {
    evenly: selectionItemsEvenly,
    percent: selectionItemsPercent,
    custom: selectionItemsCustom,
  }
  const selectionGroupItems = selectionItems[selectedTenderType]

  const primaryText = customEntry ? customOutput : (selectedTenderAmount?.text ?? centsToDollars(0))

  const secondaryTextComponent = (
    <div className={styles.priceContainer}>
      <div className={styles.costWithLabel}>
        <div className={styles.balanceDue}>Total:</div>
        <div className={styles.amount}>{totalDisplay}</div>
      </div>
      <div className={styles.costWithLabel}>
        <div className={styles.balanceDue}>Balance Due:</div>
        <div className={styles.amount}>{balanceDueDisplay}</div>
      </div>
    </div>
  )

  const secondaryText = isMobile
    ? secondaryTextComponent
    : `Total: ${centsToDollars(order?.amountInCents)} • Balance Due: ${balanceDueDisplay}`

  const noBalanceDue = effectiveBalanceDueInCents === 0

  const triggerPopAnimation = () => {
    setShouldAnimatePop(true)
    setTimeout(() => setShouldAnimatePop(false), 100)
  }

  const handleTenderTypeDidChange = (value) => {
    if (selectedTenderType !== value && !shouldAnimateSlide) {
      setShouldAnimateSlide(true)
    }

    setTimeout(() => {
      onCustomInput(0, true)
      setSelectedTenderType(value)
      setSelectedTenderAmount(undefined)
      setShouldAnimateSlide(false)
    }, 100)
  }

  const handleTenderAmountDidChange = (item, index) => {
    setSelectedTenderAmount({ ...item, index })

    if (selectedTenderAmount?.index !== index && !shouldAnimatePop) {
      triggerPopAnimation()
    }
  }

  const handleNewPaymentConfirmed = () => {
    if (noBalanceDue) {
    } else if (customEntry) {
      if (customIsValid) {
        onConfirm(+customRawValue, order)
      } else {
        ToastManager.error('Please enter a valid amount before proceeding')
      }
    } else if (selectedTenderAmount?.amountInCents > 0) {
      onConfirm(Math.round(selectedTenderAmount?.amountInCents), order)
    } else {
      ToastManager.error('Please enter a valid amount before proceeding')
    }
  }

  const handleOnConfirm = () => {
    //if we're on CFD mode then we need to reset the current player flow to split in case
    //go back was pressed in tender selection
    if (cfdMode === MODES.POS) {
      dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.SPLIT_PAY, true))
    }

    handleNewPaymentConfirmed()
  }

  let confirmButtonText = 'Next'

  if (noBalanceDue) {
    confirmButtonText = 'Next'
  }

  if (isEmpty(order)) {
    return <VNLoader />
  }

  return (
    <div className={cn(styles.SplitTender, className)}>
      <VNConcessionsNavBar textDisplay={isMobile && primaryText} onClick={() => onGoBack(lastPaymentId, keepOrderInProgress)} />
      <GratuityHeader className={styles.gratuityHeader} primaryText={!isMobile && primaryText} secondaryText={secondaryText} />
      <div className={styles.SplitTender__splitChoices}>
        <SegmentedControlV2
          className={styles.segmentedControl}
          items={splitTenderChoices}
          selectedValue={selectedTenderType}
          onSelect={handleTenderTypeDidChange}
          disabled={noBalanceDue}
        />
      </div>
      <div className={styles.SplitTender__body}>
        <div className={cn(styles.splitChoicesContent, { [styles.slide]: shouldAnimateSlide })}>
          <SelectionGroup
            className={cn(styles.selectionGroup, { [styles.isHidden]: customEntry })}
            items={selectionGroupItems}
            selectedIndex={selectedTenderAmount?.index}
            onChange={handleTenderAmountDidChange}
          />
          <NumberPadV2
            className={cn(styles.numberPad, { [styles.isHidden]: !customEntry })}
            backspace="true"
            disabled={noBalanceDue}
            onTapButton={onCustomInput}
            isCashTender={true}
          />
        </div>
      </div>
      <div className={styles.bottomButtonContainer}>
        <VNBottomButton text={confirmButtonText} onClick={handleOnConfirm} />
      </div>
    </div>
  )
}

const mapStateToProps = (state, props) => {
  let order = getOrderInProgress(state)

  const orderPayments = order.payments?.filter((payment) => payment.userAttributes?.transType !== 'auth')
  const lastPayment = last(orderPayments)

  const effectiveBalanceDueInCents = order?.balanceDueInCents
    ? order.balanceDueInCents
    : order?.amountInCents - lastPayment?.amountInCents
  const balanceDueDisplay = numberAsCurrency(effectiveBalanceDueInCents / 100.0)

  const lastPaymentId = lastPayment?.paymentId

  return {
    orderId: order?.uuid,
    lastPaymentId,
    totalAmountInCents: order?.amountInCents,
    totalDisplay: centsToDollars(order?.amountInCents),
    balanceDueDisplay,
    effectiveBalanceDueInCents: effectiveBalanceDueInCents,
    order,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const push = props?.history?.push ?? function () {}
  const goBack = props?.history?.goBack ?? function () {}

  return {
    onConfirm: (tenderAmountInCents, order) => {
      const fromSplit = true
      push({
        pathname: '/tender-selection',
        state: { tenderAmountInCents, order, fromSplit },
      })
    },
    onGoBack: (lastPaymentId, keepOrderInProgress) => {
      dispatch(updateTransactionLifecycle(CFD_EVENTS.IDLE))
      dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))

      if (lastPaymentId) {
        push(`/receipt/split-tender/${lastPaymentId}`)
      } else {
        if (!keepOrderInProgress) {
          dispatch(clearOrderInProgress())
        } else {
          dispatch(clearTipOrderInProgress())
        }

        goBack()
      }
    },
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SplitTender))
